// ═══════════════════════════════════════════════════════════════════════════════
// SOCIAL POSTING FLOW - Composant pour le flow de publication via Abra
// Upload image/vidéo → Transcription → Génération texte → Choix destination → Publication
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Video, Image, X, Loader2, Sparkles,
  Globe, Users, Check, ChevronRight, Link2,
  RefreshCw, Send, AlertCircle, Edit3, Clock, Lock, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// ÉTAPES DU FLOW
// ═══════════════════════════════════════════════════════════════════════════════

const STEPS = {
  UPLOAD: 'upload',
  PROCESSING: 'processing',
  EDIT_TEXT: 'edit_text',
  CHOOSE_DESTINATION: 'destination',
  ADD_LINKS: 'links',
  CONFIRM: 'confirm',
  PUBLISHING: 'publishing',
  SUCCESS: 'success'
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function SocialPostingFlow({ onClose, onComplete, userId }) {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [editableText, setEditableText] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('public');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [linkedProduct, setLinkedProduct] = useState(null);
  const [linkedService, setLinkedService] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [videoUsage, setVideoUsage] = useState({ used: 0, remaining: 10 });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // États pour la planification (Superpouvoir)
  const [publishMode, setPublishMode] = useState('now'); // now ou scheduled
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [hasSchedulingAccess, setHasSchedulingAccess] = useState(false);
  
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleFileSelect = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError(null);
    setMediaFile(file);
    setMediaType(type);
    
    // Créer la prévisualisation
    const preview = URL.createObjectURL(file);
    setMediaPreview(preview);
    
    // Passer à l'étape de traitement
    setStep(STEPS.PROCESSING);
    setIsLoading(true);
    
    try {
      // Upload et traitement du média
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      const uploadResponse = await fetch(`${API_URL}/api/social/upload-media`, {
        method: 'POST',
        body: formData
      });
      
      const uploadData = await uploadResponse.json();
      
      if (uploadData.status === 'quota_exceeded') {
        setError(uploadData.message);
        setStep(STEPS.UPLOAD);
        setIsLoading(false);
        return;
      }
      
      if (uploadData.status === 'video_too_long') {
        setError(uploadData.message);
        setStep(STEPS.UPLOAD);
        setIsLoading(false);
        return;
      }
      
      // Mise à jour du quota vidéo
      if (uploadData.videos_used_this_month !== undefined) {
        setVideoUsage({
          used: uploadData.videos_used_this_month,
          remaining: uploadData.videos_remaining
        });
      }
      
      // Si vidéo, récupérer la transcription
      if (type === 'video' && uploadData.transcription) {
        setTranscription(uploadData.transcription);
      }
      
      // Générer le texte optimisé
      const genResponse = await fetch(`${API_URL}/api/social/generate-post-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          media_description: uploadData.transcription || `${type === 'image' ? 'Photo' : 'Vidéo'} de projet`,
          tone: 'professionnel',
          target: 'public',
          include_cta: true
        })
      });
      
      const genData = await genResponse.json();
      
      if (genData.status === 'success') {
        setGeneratedText(genData.generated_text);
        setEditableText(genData.generated_text);
      } else {
        setEditableText(genData.suggested_text || 'Découvrez mon dernier projet !');
      }
      
      // Récupérer les groupes disponibles
      const groupsResponse = await fetch(`${API_URL}/api/social/user-groups/${userId}`);
      const groupsData = await groupsResponse.json();
      setAvailableGroups(groupsData.groups || []);
      
      // Récupérer les produits/services
      const productsResponse = await fetch(`${API_URL}/api/social/user-products/${userId}`);
      const productsData = await productsResponse.json();
      setAvailableProducts(productsData.products || []);
      setAvailableServices(productsData.services || []);
      
      setStep(STEPS.EDIT_TEXT);
      
    } catch (err) {
      console.error('Processing error:', err);
      setError('Erreur lors du traitement. Réessaie.');
      setStep(STEPS.UPLOAD);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRegenerateText = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/social/generate-post-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          media_description: transcription || `${mediaType === 'image' ? 'Photo' : 'Vidéo'} de projet`,
          tone: 'enthousiaste',
          target: selectedDestination,
          include_cta: selectedDestination === 'public'
        })
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setGeneratedText(data.generated_text);
        setEditableText(data.generated_text);
      }
    } catch (err) {
      console.error('Regenerate error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePublish = async () => {
    setStep(STEPS.PUBLISHING);
    setIsLoading(true);
    setError(null);
    
    try {
      // TODO: Implémenter l'upload final du média vers le storage
      // Pour l'instant, simuler avec un délai
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (publishMode === 'scheduled' && scheduledDate && scheduledTime) {
        // Publication planifiée
        const scheduledDateTime = `${scheduledDate}T${scheduledTime}:00`;
        
        const response = await fetch(`${API_URL}/api/social/schedule-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            content: editableText,
            media_url: mediaPreview,
            media_type: mediaType,
            target_type: selectedDestination === 'public' ? 'public' : 'group',
            target_group_id: selectedGroupId,
            linked_product_id: linkedProduct?.id,
            linked_service_id: linkedService?.id,
            scheduled_for: scheduledDateTime,
            timezone: 'Europe/Paris'
          })
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          setStep(STEPS.SUCCESS);
        } else if (data.status === 'superpouvoir_required') {
          setError(data.message);
          setStep(STEPS.CONFIRM);
        } else {
          setError(data.message || 'Erreur lors de la planification. Réessaie.');
          setStep(STEPS.CONFIRM);
        }
      } else {
        // Publication immédiate
        const response = await fetch(`${API_URL}/api/social/create-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            content: editableText,
            media_url: mediaPreview, // À remplacer par l'URL du storage
            media_type: mediaType,
            target_type: selectedDestination === 'public' ? 'public' : 'group',
            target_group_id: selectedGroupId,
            linked_product_id: linkedProduct?.id,
            linked_service_id: linkedService?.id
          })
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          setStep(STEPS.SUCCESS);
        } else {
          setError('Erreur lors de la publication. Réessaie.');
          setStep(STEPS.CONFIRM);
        }
      }
    } catch (err) {
      console.error('Publish error:', err);
      setError('Erreur de connexion. Réessaie.');
      setStep(STEPS.CONFIRM);
    } finally {
      setIsLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDUS DES ÉTAPES
  // ═══════════════════════════════════════════════════════════════════════════
  
  const renderUploadStep = () => (
    <div className="p-6 text-center" data-testid="social-post-upload-step">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center">
        <Upload className="w-8 h-8 text-white" />
      </div>
      
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        Crée ton post avec ton assistant
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        Envoie une image ou une vidéo (max 90s), je m'occupe du texte !
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center gap-2 p-4 bg-orange-50 hover:bg-orange-100 border-2 border-dashed border-orange-200 rounded-xl transition-colors"
          data-testid="upload-image-btn"
        >
          <Image className="w-8 h-8 text-[#F97316]" />
          <span className="text-sm font-medium text-gray-700">Image</span>
        </button>
        
        <button
          onClick={() => videoInputRef.current?.click()}
          className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 border-2 border-dashed border-blue-200 rounded-xl transition-colors"
          data-testid="upload-video-btn"
        >
          <Video className="w-8 h-8 text-[#3B82F6]" />
          <span className="text-sm font-medium text-gray-700">Vidéo</span>
          <span className="text-[10px] text-gray-400">Max 90s</span>
        </button>
      </div>
      
      {/* Compteur vidéos */}
      <div className="text-xs text-gray-400">
        Vidéos ce mois : {videoUsage.used}/10 utilisées • {videoUsage.remaining} restantes
      </div>
      
      {/* Inputs cachés */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'image')}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e, 'video')}
      />
    </div>
  );
  
  const renderProcessingStep = () => (
    <div className="p-6 text-center" data-testid="social-post-processing-step">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        Ton assistant travaille...
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {mediaType === 'video' 
          ? 'Transcription de la vidéo et génération du texte...' 
          : 'Analyse de l\'image et génération du texte...'}
      </p>
      
      {/* Aperçu du média */}
      {mediaPreview && (
        <div className="max-w-[200px] mx-auto rounded-lg overflow-hidden border border-gray-200">
          {mediaType === 'video' ? (
            <video src={mediaPreview} className="w-full" muted />
          ) : (
            <img src={mediaPreview} alt="Aperçu" className="w-full" />
          )}
        </div>
      )}
    </div>
  );
  
  const renderEditTextStep = () => (
    <div className="p-4" data-testid="social-post-edit-step">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-800">Texte généré par ton assistant</h3>
          <p className="text-xs text-gray-400">Modifie si tu veux</p>
        </div>
      </div>
      
      {/* Aperçu média compact */}
      {mediaPreview && (
        <div className="mb-3 flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
            {mediaType === 'video' ? (
              <video src={mediaPreview} className="w-full h-full object-cover" muted />
            ) : (
              <img src={mediaPreview} alt="Aperçu" className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{mediaFile?.name}</p>
            {transcription && (
              <p className="text-xs text-blue-500 mt-1 line-clamp-2">
                🎤 "{transcription.slice(0, 60)}..."
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Zone de texte éditable */}
      <textarea
        value={editableText}
        onChange={(e) => setEditableText(e.target.value)}
        className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
        rows={5}
        placeholder="Écris ton texte de post..."
        data-testid="post-text-input"
      />
      
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-400">{editableText.length} caractères</span>
        <button
          onClick={handleRegenerateText}
          disabled={isLoading}
          className="flex items-center gap-1 text-xs text-[#1E3A5F] hover:text-[#F97316] disabled:opacity-50"
        >
          <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
          Régénérer
        </button>
      </div>
      
      <button
        onClick={() => setStep(STEPS.CHOOSE_DESTINATION)}
        disabled={!editableText.trim()}
        className="w-full mt-4 py-3 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="next-step-btn"
      >
        Choisir où publier
        <ChevronRight size={18} />
      </button>
    </div>
  );
  
  const renderDestinationStep = () => (
    <div className="p-4" data-testid="social-post-destination-step">
      <h3 className="font-bold text-gray-800 mb-4">Où veux-tu publier ?</h3>
      
      {/* Option publique */}
      <button
        onClick={() => {
          setSelectedDestination('public');
          setSelectedGroupId(null);
        }}
        className={`w-full p-4 mb-3 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
          selectedDestination === 'public' 
            ? 'border-[#F97316] bg-orange-50' 
            : 'border-gray-200 hover:border-gray-300'
        }`}
        data-testid="destination-public"
      >
        <div className="w-10 h-10 rounded-full bg-[#F97316]/20 flex items-center justify-center">
          <Globe className="w-5 h-5 text-[#F97316]" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-800">Fil public - Univers Bâtiment</p>
          <p className="text-xs text-gray-500">Visible par toute la communauté</p>
        </div>
        {selectedDestination === 'public' && <Check className="w-5 h-5 text-[#F97316]" />}
      </button>
      
      {/* Options groupes privés */}
      {availableGroups.length > 0 && (
        <>
          <p className="text-xs text-gray-400 mb-2 mt-4">Ou dans un de tes groupes privés :</p>
          {availableGroups.map((group) => (
            <button
              key={group.id}
              onClick={() => {
                setSelectedDestination('group');
                setSelectedGroupId(group.id);
              }}
              className={`w-full p-3 mb-2 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
                selectedGroupId === group.id 
                  ? 'border-[#3B82F6] bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`destination-group-${group.id}`}
            >
              <div className="w-8 h-8 rounded-full bg-[#1E3A5F]/20 flex items-center justify-center text-lg">
                {group.emoji || '👥'}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800 text-sm">{group.name}</p>
              </div>
              {selectedGroupId === group.id && <Check className="w-5 h-5 text-[#3B82F6]" />}
            </button>
          ))}
        </>
      )}
      
      <div className="flex gap-2 mt-4">
        <button
          onClick={() => setStep(STEPS.EDIT_TEXT)}
          className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 text-sm"
        >
          Retour
        </button>
        <button
          onClick={() => setStep(availableProducts.length > 0 || availableServices.length > 0 ? STEPS.ADD_LINKS : STEPS.CONFIRM)}
          className="flex-1 py-2.5 bg-[#F97316] text-white rounded-xl font-medium text-sm flex items-center justify-center gap-1"
          data-testid="next-confirm-btn"
        >
          Continuer
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
  
  const renderLinksStep = () => (
    <div className="p-4" data-testid="social-post-links-step">
      <h3 className="font-bold text-gray-800 mb-2">Ajouter un lien produit/prestation ?</h3>
      <p className="text-xs text-gray-500 mb-4">Optionnel - permet aux visiteurs de te contacter</p>
      
      {availableProducts.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Tes produits</p>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableProducts.map((product) => (
              <button
                key={product.id}
                onClick={() => setLinkedProduct(linkedProduct?.id === product.id ? null : product)}
                className={`w-full p-2 rounded-lg border text-left text-sm flex items-center justify-between ${
                  linkedProduct?.id === product.id 
                    ? 'border-[#F97316] bg-orange-50' 
                    : 'border-gray-200'
                }`}
              >
                <span>{product.name}</span>
                {product.price && <span className="text-gray-500">{product.price}€</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {availableServices.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Tes prestations</p>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {availableServices.map((service) => (
              <button
                key={service.id}
                onClick={() => setLinkedService(linkedService?.id === service.id ? null : service)}
                className={`w-full p-2 rounded-lg border text-left text-sm flex items-center justify-between ${
                  linkedService?.id === service.id 
                    ? 'border-[#3B82F6] bg-blue-50' 
                    : 'border-gray-200'
                }`}
              >
                <span>{service.name}</span>
                {service.price && <span className="text-gray-500">{service.price}€</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex gap-2 mt-4">
        <button
          onClick={() => setStep(STEPS.CHOOSE_DESTINATION)}
          className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 text-sm"
        >
          Retour
        </button>
        <button
          onClick={() => setStep(STEPS.CONFIRM)}
          className="flex-1 py-2.5 bg-[#F97316] text-white rounded-xl font-medium text-sm"
          data-testid="skip-links-btn"
        >
          {linkedProduct || linkedService ? 'Continuer' : 'Passer'}
        </button>
      </div>
    </div>
  );
  
  const renderConfirmStep = () => (
    <div className="p-4" data-testid="social-post-confirm-step">
      <h3 className="font-bold text-gray-800 mb-4">Prêt à publier ?</h3>
      
      {/* Récapitulatif */}
      <div className="bg-gray-50 rounded-xl p-3 mb-4">
        {/* Média */}
        {mediaPreview && (
          <div className="w-full aspect-video rounded-lg overflow-hidden mb-3 bg-gray-200">
            {mediaType === 'video' ? (
              <video src={mediaPreview} className="w-full h-full object-cover" controls />
            ) : (
              <img src={mediaPreview} alt="Aperçu" className="w-full h-full object-cover" />
            )}
          </div>
        )}
        
        {/* Texte */}
        <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{editableText}</p>
        
        {/* Destination */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {selectedDestination === 'public' ? (
            <>
              <Globe size={14} />
              <span>Fil public - Univers Bâtiment</span>
            </>
          ) : (
            <>
              <Users size={14} />
              <span>Groupe : {availableGroups.find(g => g.id === selectedGroupId)?.name}</span>
            </>
          )}
        </div>
        
        {/* Liens */}
        {(linkedProduct || linkedService) && (
          <div className="mt-2 flex items-center gap-2 text-xs text-[#F97316]">
            <Link2 size={14} />
            <span>Lié à : {linkedProduct?.name || linkedService?.name}</span>
          </div>
        )}
      </div>
      
      {/* Options de publication : Maintenant ou Planifier */}
      <div className="mb-4 space-y-2">
        <button
          onClick={() => setPublishMode('now')}
          className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
            publishMode === 'now' 
              ? 'border-[#F97316] bg-orange-50' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Send size={18} className={publishMode === 'now' ? 'text-[#F97316]' : 'text-gray-400'} />
          <span className={`text-sm font-medium ${publishMode === 'now' ? 'text-gray-800' : 'text-gray-600'}`}>
            Publier maintenant
          </span>
          {publishMode === 'now' && <Check size={16} className="ml-auto text-[#F97316]" />}
        </button>
        
        <button
          onClick={() => {
            if (hasSchedulingAccess) {
              setPublishMode('scheduled');
            } else {
              // Afficher info Superpouvoir
              setError("La planification est un Superpouvoir Pro. Passe en mode Pro pour l'activer !");
            }
          }}
          className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
            publishMode === 'scheduled' 
              ? 'border-[#3B82F6] bg-blue-50' 
              : 'border-gray-200 hover:border-gray-300'
          } ${!hasSchedulingAccess ? 'opacity-60' : ''}`}
        >
          <Clock size={18} className={publishMode === 'scheduled' ? 'text-[#3B82F6]' : 'text-gray-400'} />
          <div className="flex-1 text-left">
            <span className={`text-sm font-medium ${publishMode === 'scheduled' ? 'text-gray-800' : 'text-gray-600'}`}>
              Planifier pour plus tard
            </span>
            {!hasSchedulingAccess && (
              <span className="ml-2 text-[10px] text-[#F97316] bg-[#F97316]/10 px-1.5 py-0.5 rounded-full">
                Superpouvoir Pro
              </span>
            )}
          </div>
          {!hasSchedulingAccess && <Lock size={14} className="text-gray-400" />}
          {publishMode === 'scheduled' && <Check size={16} className="ml-auto text-[#3B82F6]" />}
        </button>
        
        {/* Sélecteurs date/heure si planification */}
        <AnimatePresence>
          {publishMode === 'scheduled' && hasSchedulingAccess && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-2 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Date</label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Heure</label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      <div className="flex gap-2">
        <button
          onClick={() => setStep(STEPS.EDIT_TEXT)}
          className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 text-sm flex items-center justify-center gap-1"
        >
          <Edit3 size={14} />
          Modifier
        </button>
        <button
          onClick={handlePublish}
          disabled={isLoading || (publishMode === 'scheduled' && (!scheduledDate || !scheduledTime))}
          className="flex-1 py-2.5 bg-gradient-to-r from-[#F97316] to-[#ea6c0a] text-white rounded-xl font-medium text-sm flex items-center justify-center gap-1 disabled:opacity-50"
          data-testid="publish-btn"
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : publishMode === 'scheduled' ? (
            <Calendar size={16} />
          ) : (
            <Send size={16} />
          )}
          {publishMode === 'scheduled' ? 'Planifier' : 'Publier'}
        </button>
      </div>
    </div>
  );
  
  const renderPublishingStep = () => (
    <div className="p-6 text-center" data-testid="social-post-publishing-step">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#F97316] to-[#ea6c0a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        {publishMode === 'scheduled' ? 'Planification en cours...' : 'Publication en cours...'}
      </h3>
      <p className="text-sm text-gray-500">
        {publishMode === 'scheduled' ? 'Ton assistant planifie ton post' : 'Ton assistant publie ton post'}
      </p>
    </div>
  );
  
  const renderSuccessStep = () => (
    <div className="p-6 text-center" data-testid="social-post-success-step">
      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
        {publishMode === 'scheduled' ? (
          <Calendar className="w-10 h-10 text-white" />
        ) : (
          <Check className="w-10 h-10 text-white" />
        )}
      </div>
      <h3 className="text-lg font-bold text-gray-800 mb-2">
        {publishMode === 'scheduled' ? 'Post planifié !' : 'Post publié !'}
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {publishMode === 'scheduled' 
          ? `Ton post sera publié le ${scheduledDate} à ${scheduledTime}.`
          : selectedDestination === 'public' 
            ? 'Ton post est visible sur le fil public.' 
            : `Ton post est partagé dans "${availableGroups.find(g => g.id === selectedGroupId)?.name}".`}
      </p>
      <button
        onClick={() => {
          onComplete?.();
          onClose?.();
        }}
        className="w-full py-3 bg-[#1E3A5F] text-white rounded-xl font-medium"
        data-testid="close-success-btn"
      >
        Super, merci ! !
      </button>
    </div>
  );
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-sm w-full max-h-[80vh] overflow-y-auto"
      data-testid="social-posting-flow"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">Créer un post</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
          data-testid="close-flow-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Contenu selon l'étape */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === STEPS.UPLOAD && renderUploadStep()}
          {step === STEPS.PROCESSING && renderProcessingStep()}
          {step === STEPS.EDIT_TEXT && renderEditTextStep()}
          {step === STEPS.CHOOSE_DESTINATION && renderDestinationStep()}
          {step === STEPS.ADD_LINKS && renderLinksStep()}
          {step === STEPS.CONFIRM && renderConfirmStep()}
          {step === STEPS.PUBLISHING && renderPublishingStep()}
          {step === STEPS.SUCCESS && renderSuccessStep()}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
